<template>
    <v-card>
        <v-card-title>
            {{ $t('kelaBatch.title') }}
            <v-spacer></v-spacer>
            <v-btn small class="mr-2" @click="searchDialog = true">
                <v-icon left small>mdi-magnify</v-icon>
                {{ $t('kelaBatch.search') }}
            </v-btn>
            <v-btn icon small @click="refresh" :loading="loading">
                <v-icon>mdi-refresh</v-icon>
            </v-btn>
        </v-card-title>

        <!-- Yhteenvetolaskurit -->
        <v-card-subtitle v-if="batches.length > 0">
            <span class="mr-4">
                <v-icon small color="grey">mdi-clock-outline</v-icon>
                {{ $t('kelaBatch.waitingCount') }}: <strong>{{ pendingCount }}</strong>
            </span>
            <span class="mr-4">
                <v-icon small color="green">mdi-check-circle-outline</v-icon>
                {{ $t('kelaBatch.processedCount') }}: <strong>{{ processedCount }}</strong>
            </span>
            <span>
                <v-icon small color="orange">mdi-alert-circle-outline</v-icon>
                {{ $t('kelaBatch.otherCount') }}: <strong>{{ otherCount }}</strong>
            </span>
        </v-card-subtitle>

        <v-data-table
            :headers="tableHeaders"
            :items="batches"
            :server-items-length="totalCount"
            :options.sync="tableOptions"
            :loading="loading"
            :footer-props="{ itemsPerPageOptions: [25, 50, 100] }"
            @click:row="selectBatch"
            class="clickable-rows"
        >
            <template v-slot:item.sentAt="{ item }">
                {{ formatDateTime(item.sentAt) }}
            </template>

            <template v-slot:item.totalCost="{ item }">
                {{ formatCurrency(item.totalCost) }}
            </template>

            <template v-slot:item.totalReimbursement="{ item }">
                {{ formatCurrency(item.totalReimbursement) }}
            </template>

            <template v-slot:item.feedbackStatus="{ item }">
                <v-chip
                    small
                    :color="feedbackStatusColor(item.feedbackStatus)"
                    dark
                >
                    {{ feedbackStatusLabel(item.feedbackStatus) }}
                </v-chip>
            </template>

            <template v-slot:item.settlementReceivedAt="{ item }">
                <v-chip
                    small
                    :color="item.settlementReceivedAt ? 'green' : 'grey'"
                    dark
                >
                    {{ item.settlementReceivedAt ? $t('kelaBatch.settlementReceived') : $t('kelaBatch.feedbackPending') }}
                </v-chip>
            </template>

            <template v-slot:item.containsCombinedTrips="{ item }">
                <v-icon small :color="item.containsCombinedTrips ? 'primary' : 'grey lighten-2'">
                    {{ item.containsCombinedTrips ? 'mdi-call-merge' : 'mdi-minus' }}
                </v-icon>
            </template>

            <template v-slot:body.append v-if="batches.length > 0">
                <tr class="font-weight-bold grey lighten-4">
                    <td colspan="6"></td>
                    <td class="text-right">{{ formatCurrency(totalCostSum) }}</td>
                    <td class="text-right">{{ formatCurrency(totalReimbursementSum) }}</td>
                    <td colspan="3"></td>
                </tr>
            </template>
        </v-data-table>

        <kela-batch-search-dialog
            v-model="searchDialog"
            :current-params="currentSearchParams"
            @search="onSearch"
        ></kela-batch-search-dialog>
    </v-card>
</template>

<script>
import moment from "moment";
import { mapGetters, mapActions } from "vuex";
import KelaBatchSearchDialog from "./KelaBatchSearchDialog";

export default {
    name: "KelaBatchTable",
    components: { KelaBatchSearchDialog },
    data() {
        return {
            searchDialog: false,
            headers: []
        };
    },
    computed: {
        ...mapGetters("kelaBatch", ["batches", "totalCount", "loading", "searchParams"]),
        currentSearchParams() {
            return this.searchParams;
        },
        tableHeaders() {
            return [
                { text: this.$t("kelaBatch.region"), value: "region", sortable: false },
                { text: this.$t("kelaBatch.year"), value: "year", sortable: false },
                { text: this.$t("kelaBatch.tilitysNro"), value: "tilitysNro", sortable: false },
                { text: this.$t("kelaBatch.siirtotunnus"), value: "siirtotunnus", sortable: false },
                { text: this.$t("kelaBatch.sentAt"), value: "sentAt", sortable: false },
                { text: this.$t("kelaBatch.transportEventCount"), value: "transportEventCount", align: "right", sortable: false },
                { text: this.$t("kelaBatch.totalCost"), value: "totalCost", align: "right", sortable: false },
                { text: this.$t("kelaBatch.totalReimbursement"), value: "totalReimbursement", align: "right", sortable: false },
                { text: this.$t("kelaBatch.feedbackStatus"), value: "feedbackStatus", sortable: false },
                { text: this.$t("kelaBatch.settlementStatus"), value: "settlementReceivedAt", sortable: false },
                { text: this.$t("kelaBatch.containsCombinedTrips"), value: "containsCombinedTrips", align: "center", sortable: false }
            ];
        },
        tableOptions: {
            get() {
                return this.$store.state.kelaBatch.options;
            },
            set(val) {
                this.$store.dispatch("kelaBatch/updateOptions", val);
            }
        },
        totalCostSum() {
            return this.batches.reduce((sum, b) => sum + (b.totalCost || 0), 0);
        },
        totalReimbursementSum() {
            return this.batches.reduce((sum, b) => sum + (b.totalReimbursement || 0), 0);
        },
        pendingCount() {
            return this.batches.filter(b => !b.feedbackStatus).length;
        },
        processedCount() {
            return this.batches.filter(b => {
                const s = b.feedbackStatus;
                return s && (s === "OK" || s.includes("Hyväksytty"));
            }).length;
        },
        otherCount() {
            return this.batches.filter(b => {
                const s = b.feedbackStatus;
                return s && s !== "OK" && !s.includes("Hyväksytty");
            }).length;
        }
    },
    methods: {
        ...mapActions("kelaBatch", ["searchBatches", "setSearchParams", "loadPage"]),
        selectBatch(batch) {
            this.$emit("select-batch", batch);
        },
        refresh() {
            this.searchBatches();
        },
        onSearch(params) {
            this.setSearchParams(params);
        },
        formatDateTime(value) {
            if (!value) return "-";
            return moment(value).format("DD.MM.YYYY HH:mm");
        },
        formatCurrency(value) {
            if (value === null || value === undefined) return "-";
            return new Intl.NumberFormat("fi-FI", {
                style: "currency",
                currency: "EUR"
            }).format(value);
        },
        feedbackStatusColor(status) {
            if (!status) return "grey";
            if (status === "OK" || status.includes("Hyväksytty")) return "green";
            if (status.includes("Virhe") || status.includes("Error")) return "red";
            return "orange";
        },
        feedbackStatusLabel(status) {
            if (!status) return this.$t("kelaBatch.feedbackPending");
            return status;
        }
    },
    mounted() {
        this.searchBatches();
    }
};
</script>

<style scoped>
.clickable-rows >>> tbody tr {
    cursor: pointer;
}
.clickable-rows >>> tbody tr:hover {
    background-color: #f5f5f5;
}
</style>
